import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Search, Clear } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { GlobalUtilContext } from '../../../../contexts';
import { fetchPartnerLogSuggestions } from '../../../../../services/entities/partner-logs';

const style = {
  position: 'absolute',
  top: '95px',
  bgcolor: 'background.paper',
  border: '1px solid rgba(155,149,159,0.26)',
  boxShadow: 20,
  borderRadius: 1,
  p: 4
};

function SearchModal({ isOpen, setIsOpen }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const { t } = useTranslation();
  const { setSearch, search, selectedService, handleSelectedPartner, isMenuOpen } =
    React.useContext(GlobalUtilContext);

  const handleClear = React.useCallback(() => {
    setSearch('');
    setSuggestions([]);
  }, [setSearch, setSuggestions]);

  const handleSearchInput = React.useCallback(
    (e) => {
      const fetchAutoSuggestions = async (searchString) => {
        const params = {
          search: searchString,
          association_type: selectedService.association_type
        };

        const result = await fetchPartnerLogSuggestions(params);
        if (result) {
          setSuggestions([...result]);
        }
      };

      setSearch(e?.target?.value);

      if (e?.target?.value?.length >= 3) {
        fetchAutoSuggestions(e?.target?.value);
      } else {
        setSuggestions([]);
      }
    },
    [setSearch, setSuggestions, selectedService]
  );

  const handleClose = React.useCallback(
    (shouldClear) => {
      setIsOpen(false);
      if (shouldClear) {
        handleClear();
      }
    },
    [setIsOpen, handleClear]
  );

  const onSubmit = React.useCallback(
    (e, selectedPartner) => {
      const handleSubmit = () => {
        handleClose(false);
        handleSelectedPartner(selectedPartner);
      };

      if (e?.key) {
        if (e.key === 'Enter') {
          handleSubmit();
        }
      } else {
        handleSubmit();
      }
    },
    [search, handleClose, setSearch]
  );

  const renderAutosuggestion = React.useCallback(
    (host) => {
      const parts = host?.split(new RegExp(`(${search})`, 'gi'));
      return (
        <>
          {parts?.map((part, i) =>
            part.toLowerCase() === search.toLowerCase() ? <span key={i}>{part}</span> : part
          )}
        </>
      );
    },
    [search]
  );

  const renderTextField = (params) => (
    <TextField
      {...params}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search color="#9B959F" onClick={onSubmit} sx={{ mr: 1, cursor: 'pointer' }} />
          </InputAdornment>
        ),
        endAdornment: search?.length ? (
          <InputAdornment>
            <Clear color="#9B959F" onClick={handleClear} sx={{ cursor: 'pointer' }} />
          </InputAdornment>
        ) : (
          ''
        ),
        disableUnderline: true
      }}
      autoFocus
      fullWidth
      id="search-input-fullWidth"
      onChange={handleSearchInput}
      onKeyDown={onSubmit}
      placeholder={t('search.search')}
      sx={{
        '& .MuiInputBase-input': {
          padding: 0,
          color: '#00010E',
          fontSize: 14,
          lineHeight: '17px'
        },
        '& .MuiInputBase-input::placeholder': {
          WebkitTextFillColor: '#9B959F',
          opacity: 1,
          fontSize: 14,
          fontWeight: 400
        },
        '& .MuiInputBase-root': {
          padding: '10px 19px',
          background: 'white'
        }
      }}
      value={search}
      variant="standard"
    />
  );

  return (
    <Modal
      aria-describedby="modal-modal-description"
      aria-labelledby="modal-modal-title"
      onClose={() => handleClose(true)}
      open={isOpen}
      sx={{
        '.MuiBox-root': {
          padding: '0 0 10px'
        }
      }}
    >
      <Box
        sx={{
          ...style,
          left: isMenuOpen ? '20%' : '10.5%',
          width: isMenuOpen ? '72.5%' : '83%'
        }}
      >
        {renderTextField()}
        {suggestions?.map?.((suggestion, index) => (
          <Box
            aria-orientation="vertical"
            key={`auto-suggestion-${index}`}
            name={`auto-suggestion-${index}`}
            onClick={() => onSubmit(undefined, suggestion)}
            onKeyDown={() => onSubmit(undefined, suggestion)}
            role="toolbar"
            sx={{
              p: '13px 17px !important',
              width: '100%',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#E2E2E2' },
              '&:focus': {
                bgcolor: '#E2E2E2',
                outline: 'none',
                border: 'none'
              }
            }}
            tabIndex={0}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                lineHeight: '17px',
                color: '#9B959F',
                span: { color: '#00010E' }
              }}
            >
              {renderAutosuggestion(suggestion?.name_or_key)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Modal>
  );
}

SearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired
};

export { SearchModal };
